import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, Download, ArrowLeft, Plus, Trash2, GitBranch, AlertTriangle,
  Eye, EyeOff, RefreshCw, Languages,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { SortableList } from '../components/SortableList';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const blankItem = () => ({ description: '', qty: 1, unit_price: 0 });
const blankSection = (lang) => ({
  title: lang === 'fr' ? 'Nouvelle section' : '',
  items: [blankItem()],
});

export default function ChangeOrderEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const lastManualSaveRef = useRef(null);
  const [editorLang, setEditorLang] = useState('en');  // 'en' | 'fr'
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    fetchCo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCo = async () => {
    try {
      const res = await fetch(`${API}/change-orders/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCo(data);
      } else {
        navigate('/change-orders');
      }
    } catch {
      navigate('/change-orders');
    } finally {
      setLoading(false);
    }
  };

  // Warn on navigation away with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  // ─── Language-aware accessors ──────────────────────────────────
  // Edits in FR view go to *_fr mirrors so EN stays clean. When in FR
  // mode and no FR content exists yet, we fall back to EN so the form
  // isn't blank — the user can then click Sync FR to translate.
  const sectionsKey = editorLang === 'fr' ? 'sections_fr' : 'sections';
  const titleKey = editorLang === 'fr' ? 'title_fr' : 'title';
  const reasonKey = editorLang === 'fr' ? 'reason_fr' : 'reason';
  const termsKey = editorLang === 'fr' ? 'payment_terms_fr' : 'payment_terms';
  const notesKey = editorLang === 'fr' ? 'notes_fr' : 'notes';

  const currentSections = (co?.[sectionsKey]?.length ? co[sectionsKey] : (co?.sections || []));
  const currentTitle = co?.[titleKey] || (editorLang === 'fr' ? '' : co?.title || '');
  const currentReason = co?.[reasonKey] || (editorLang === 'fr' ? '' : co?.reason || '');
  const currentTerms = co?.[termsKey] || (editorLang === 'fr' ? '' : co?.payment_terms || '');
  const currentNotes = co?.[notesKey] || (editorLang === 'fr' ? '' : co?.notes || '');

  const setField = (field, value) => {
    setCo(prev => ({ ...prev, [field]: value }));
    setHasUnsaved(true);
  };

  // Write to the language-appropriate mirror field (e.g. title vs title_fr).
  const setLangField = (baseField, value) => {
    const key = editorLang === 'fr' ? `${baseField}_fr` : baseField;
    setCo(prev => ({ ...prev, [key]: value }));
    setHasUnsaved(true);
  };

  const setSection = (sIdx, field, value) => {
    setCo(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[sIdx] = { ...sections[sIdx], [field]: value };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsaved(true);
  };

  const setItem = (sIdx, iIdx, field, value) => {
    setCo(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      const items = [...sections[sIdx].items];
      items[iIdx] = {
        ...items[iIdx],
        [field]: field === 'qty' || field === 'unit_price' ? parseFloat(value) || 0 : value,
      };
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsaved(true);
  };

  const addSection = () => {
    setCo(prev => ({
      ...prev,
      [sectionsKey]: [...(prev[sectionsKey] || prev.sections || []), blankSection(editorLang)],
    }));
    setHasUnsaved(true);
  };

  const removeSection = (sIdx) => {
    setCo(prev => ({
      ...prev,
      [sectionsKey]: (prev[sectionsKey] || prev.sections || []).filter((_, i) => i !== sIdx),
    }));
    setHasUnsaved(true);
  };

  const addItem = (sIdx) => {
    setCo(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[sIdx] = { ...sections[sIdx], items: [...sections[sIdx].items, blankItem()] };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsaved(true);
  };

  const removeItem = (sIdx, iIdx) => {
    setCo(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[sIdx] = {
        ...sections[sIdx],
        items: sections[sIdx].items.filter((_, i) => i !== iIdx),
      };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsaved(true);
  };

  const toggleCredit = (sIdx, iIdx) => {
    setCo(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      const items = [...sections[sIdx].items];
      const cur = items[iIdx];
      items[iIdx] = { ...cur, qty: (cur.qty || 0) * -1 };
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsaved(true);
  };

  const handleSave = useCallback(async () => {
    if (!co || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/change-orders/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: co.title,
          reason: co.reason,
          sections: co.sections,
          status: co.status,
          tax_gst: co.tax_gst,
          tax_qst: co.tax_qst,
          payment_terms: co.payment_terms,
          notes: co.notes,
          title_fr: co.title_fr,
          reason_fr: co.reason_fr,
          sections_fr: co.sections_fr,
          payment_terms_fr: co.payment_terms_fr,
          notes_fr: co.notes_fr,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setCo(prev => ({ ...prev, updated_at: updated.updated_at }));
      } else {
        toast.error('Failed to save');
      }
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [co, id, saving]);

  const handleManualSave = async () => {
    await handleSave();
    lastManualSaveRef.current = new Date();
    setHasUnsaved(false);
    toast.success('Saved');
    if (showPreview) loadPreview();
  };

  // Auto-save every 30 s
  useEffect(() => {
    if (!co || loading) return;
    const t = setInterval(() => handleSave(), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSave, loading]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API}/change-orders/${id}/pdf?lang=${editorLang}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      }
    } catch { /* ignore */ }
    finally { setPreviewLoading(false); }
  }, [id, editorLang]);

  useEffect(() => {
    if (!showPreview) { setPdfUrl(null); return; }
    loadPreview();
  }, [showPreview, loadPreview]);

  // Refresh preview when the editor language flips while preview is open
  useEffect(() => {
    if (showPreview) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorLang]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const handleExportPdf = async () => {
    try {
      const res = await fetch(`${API}/change-orders/${id}/pdf?lang=${editorLang}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${co.change_order_number}${editorLang === 'fr' ? '-FR' : ''}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      }
    } catch { toast.error('Failed to export PDF'); }
  };

  const handleTranslate = async () => {
    const srcLang = editorLang === 'fr' ? 'fr' : 'en';
    const tgtLang = srcLang === 'fr' ? 'en' : 'fr';
    await runTranslate(srcLang, tgtLang);
  };

  // Toggle language. If switching to a language that has no mirror content
  // yet, kick off translation automatically so the form actually shows the
  // translated text — toggle alone should "just work" for the user.
  const handleLangToggle = async (nextLang) => {
    if (nextLang === editorLang) return;
    setEditorLang(nextLang);
    const hasMirror = nextLang === 'fr'
      ? (co?.sections_fr && co.sections_fr.length > 0)
      : (co?.sections && co.sections.length > 0 && (co?.title || '').length > 0);
    // No mirror content → auto-translate from the *current* language (which
    // is the source we have content in).
    if (!hasMirror && !translating) {
      // Tiny delay lets the toggle render its new active state first.
      await new Promise(r => setTimeout(r, 50));
      await runTranslate(editorLang /* source = the lang we were on */, nextLang);
    }
  };

  const runTranslate = async (srcLang, tgtLang) => {
    if (translating) return;
    setTranslating(true);
    try {
      await handleSave();
      const res = await fetch(`${API}/ai/translate-change-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ change_order_id: id, source_lang: srcLang, target_lang: tgtLang }),
      });
      if (res.ok) {
        const data = await res.json();
        delete data._cache_stats;
        setCo(prev => ({ ...prev, ...data }));
        setHasUnsaved(false);
        toast.success(`Translated to ${tgtLang === 'fr' ? 'French' : 'English'}`);
        if (showPreview) loadPreview();
      } else {
        let detail = 'Translation failed';
        try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
        toast.error(detail);
      }
    } catch {
      toast.error('Translation request failed.');
    } finally {
      setTranslating(false);
    }
  };

  const handleBack = () => {
    if (hasUnsaved) {
      setPendingNav('/change-orders');
      setShowLeaveDialog(true);
    } else {
      navigate('/change-orders');
    }
  };

  if (loading || !co) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ─── Calculations ───────────────────────────────────────────────
  // Use the EN sections as the source of truth for totals (prices live on
  // the EN side; the FR mirror only carries translated descriptions).
  const calcSections = co.sections || [];
  let subtotal = 0;
  const subtotalsPerSection = calcSections.map((s) => {
    let t = 0;
    (s.items || []).forEach(it => { t += (it.qty || 0) * (it.unit_price || 0); });
    subtotal += t;
    return t;
  });
  // When viewing FR, we still want to show per-section subtotals next to the
  // FR-titled sections. Align the totals array to the currently-rendered list.
  const renderSubtotals = currentSections.map((_, sIdx) => {
    // If FR sections diverge from EN length, fall back to recalculating.
    if (sIdx < subtotalsPerSection.length) return subtotalsPerSection[sIdx];
    let t = 0;
    (currentSections[sIdx]?.items || []).forEach(it => {
      t += (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
    });
    return t;
  });
  const gst = subtotal * (co.tax_gst || 5) / 100;
  const qst = subtotal * (co.tax_qst || 9.975) / 100;
  const total = subtotal + gst + qst;
  const isCredit = total < 0;

  return (
    <div className="animate-fade-in" data-testid="change-order-editor">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="text-[#6B7A73] hover:bg-[#F0F3F1] -ml-2"
            data-testid="co-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded text-xs font-bold text-amber-800 uppercase tracking-wider">
                <GitBranch className="w-3 h-3" />
                Change Order
              </span>
              <span
                className="text-xs text-[#6B7A73]"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {co.change_order_number}
              </span>
            </div>
            <h1
              className="text-xl sm:text-2xl font-semibold text-[#1A1D1C] mt-1 truncate"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              {currentTitle || (editorLang === 'fr' ? 'Avenant sans titre' : 'Untitled Change Order')}
            </h1>
            <p className="text-xs text-[#6B7A73] mt-0.5">
              Modifies{' '}
              <button
                onClick={() => navigate(`/estimates/${co.parent_estimate_id}`)}
                className="text-[#2D6B68] hover:underline"
                data-testid="co-parent-link"
              >
                {co.parent_estimate_number}
              </button>
              {' '}· Client: {co.client?.company || co.client?.name || '—'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Language toggle */}
          <div className="flex items-center bg-white border border-[#E6EBE8] rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => handleLangToggle('en')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${editorLang === 'en' ? 'bg-[#2D6B68] text-white' : 'text-[#6B7A73] hover:bg-[#F5F6F5]'} ${translating ? 'opacity-60 pointer-events-none' : ''}`}
              data-testid="co-lang-en"
            >EN</button>
            <button
              type="button"
              onClick={() => handleLangToggle('fr')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${editorLang === 'fr' ? 'bg-[#2D6B68] text-white' : 'text-[#6B7A73] hover:bg-[#F5F6F5]'} ${translating ? 'opacity-60 pointer-events-none' : ''}`}
              data-testid="co-lang-fr"
            >FR</button>
          </div>

          <Select value={co.status} onValueChange={v => setField('status', v)}>
            <SelectTrigger className="w-32 bg-white border-[#E6EBE8] rounded-md text-sm" data-testid="co-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            onClick={handleTranslate}
            disabled={translating}
            variant="outline"
            className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
            data-testid="co-translate-btn"
          >
            <Languages className="w-4 h-4 mr-2" />
            {translating ? 'Syncing…' : (editorLang === 'fr' ? 'Sync EN' : 'Sync FR')}
          </Button>

          <Button
            data-testid="co-save-btn"
            onClick={handleManualSave}
            disabled={saving}
            className={hasUnsaved
              ? 'bg-white border border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium'
              : 'bg-[#2D6B68] border border-[#2D6B68] text-white rounded-md px-4 py-2 font-medium'
            }
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : (hasUnsaved ? 'Save' : 'Saved')}
          </Button>
          <Button
            data-testid="co-export-pdf-btn"
            onClick={handleExportPdf}
            variant="outline"
            className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium"
          >
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button
            data-testid="co-preview-toggle-btn"
            onClick={() => setShowPreview(s => !s)}
            variant={showPreview ? 'default' : 'outline'}
            className={showPreview
              ? 'bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-4 py-2 font-medium'
              : 'border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-4 py-2 font-medium'
            }
          >
            {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showPreview ? 'Close Preview' : 'Preview'}
          </Button>
        </div>
      </div>

      <div className={`flex gap-6 ${showPreview ? '' : ''}`}>
        {/* Main content column */}
        <div className={`${showPreview ? 'w-1/2' : 'w-full'} transition-all duration-300 space-y-6`}>
          {/* Basics card */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">
                {editorLang === 'fr' ? 'Détails de l\'avenant' : 'Change Order Details'}
              </h2>
              <span className="text-[10px] uppercase tracking-wider text-[#6B7A73] bg-[#F0F3F1] border border-[#E6EBE8] px-2 py-0.5 rounded">
                {editorLang === 'fr' ? 'Édition en français' : 'Editing in English'}
              </span>
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7A73] mb-1 block">
                {editorLang === 'fr' ? 'Titre' : 'Title'}
              </label>
              <Input
                data-testid="co-title-input"
                value={currentTitle}
                onChange={e => setLangField('title', e.target.value)}
                placeholder={editorLang === 'fr'
                  ? 'p. ex. Ajout d\'un garde-manger + comptoirs améliorés'
                  : 'e.g. Added pantry + upgraded countertops'}
                className="bg-white border-[#E6EBE8]"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7A73] mb-1 block">
                {editorLang === 'fr' ? 'Motif du changement' : 'Reason for change'}
              </label>
              <Textarea
                data-testid="co-reason-input"
                value={currentReason}
                onChange={e => setLangField('reason', e.target.value)}
                placeholder={editorLang === 'fr'
                  ? 'Décrivez brièvement la raison du changement. Affiché sur le PDF sous Résumé du changement.'
                  : 'Briefly describe why this change is needed. Shown on the PDF under Change Summary.'}
                rows={3}
                className="bg-white border-[#E6EBE8] resize-none"
              />
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-4">
            <SortableList
              items={currentSections}
              onReorder={(newOrder) => setCo(prev => ({ ...prev, [sectionsKey]: newOrder }))}
              getId={(_, i) => `co-section-${i}`}
              renderItem={(section, sIdx) => {
                const sTotal = renderSubtotals[sIdx] || 0;
                const sIsCredit = sTotal < 0;
                return (
                  <div
                    className="bg-white border border-[#E6EBE8] rounded-md p-5 space-y-3 mb-4"
                    data-testid={`co-section-${sIdx}`}
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        value={section.title || ''}
                        onChange={e => setSection(sIdx, 'title', e.target.value)}
                        placeholder={editorLang === 'fr' ? `Titre de section ${sIdx + 1}` : `Section ${sIdx + 1} title`}
                        className="font-semibold border-none shadow-none text-base bg-transparent focus-visible:ring-1 focus-visible:ring-[#2D6B68] px-2"
                        data-testid={`co-section-title-${sIdx}`}
                      />
                      <button
                        onClick={() => removeSection(sIdx)}
                        className="p-1.5 rounded-md text-[#6B7A73] hover:text-red-600 hover:bg-red-50 transition-colors"
                        title={editorLang === 'fr' ? 'Supprimer la section' : 'Remove section'}
                        data-testid={`co-remove-section-${sIdx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Items header (grid) */}
                    <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider text-[#6B7A73] font-semibold border-b border-[#E6EBE8] pb-2 px-2">
                      <span className="col-span-6">{editorLang === 'fr' ? 'Description' : 'Description'}</span>
                      <span className="col-span-1 text-right">{editorLang === 'fr' ? 'Qté' : 'Qty'}</span>
                      <span className="col-span-2 text-right">{editorLang === 'fr' ? 'P.U. $' : 'Unit $'}</span>
                      <span className="col-span-1 text-right">{editorLang === 'fr' ? 'Ligne $' : 'Line $'}</span>
                      <span className="col-span-2"></span>
                    </div>
                    {/* Items — drag to reorder */}
                    <SortableList
                      items={section.items || []}
                      onReorder={(newItems) => setCo(prev => {
                        const sections = [...(prev[sectionsKey] || prev.sections || [])];
                        sections[sIdx] = { ...sections[sIdx], items: newItems };
                        return { ...prev, [sectionsKey]: sections };
                      })}
                      getId={(_, iIdx) => `co-item-${sIdx}-${iIdx}`}
                      renderItem={(item, iIdx) => {
                        const line = (item.qty || 0) * (item.unit_price || 0);
                        const itemIsCredit = (item.qty || 0) < 0;
                        return (
                          <div className="grid grid-cols-12 gap-2 items-start py-1.5 px-2 border-b border-[#F0F3F1]">
                            <div className="col-span-6">
                              <Textarea
                                value={item.description || ''}
                                onChange={e => setItem(sIdx, iIdx, 'description', e.target.value)}
                                rows={1}
                                className="border-[#E6EBE8] bg-white text-sm min-h-0 py-1.5 resize-none w-full"
                                data-testid={`co-item-desc-${sIdx}-${iIdx}`}
                              />
                            </div>
                            <Input
                              type="number"
                              step="any"
                              value={item.qty ?? 0}
                              onChange={e => setItem(sIdx, iIdx, 'qty', e.target.value)}
                              className="col-span-1 border-[#E6EBE8] bg-white text-right text-sm"
                              data-testid={`co-item-qty-${sIdx}-${iIdx}`}
                            />
                            <Input
                              type="number"
                              step="0.01"
                              value={item.unit_price ?? 0}
                              onChange={e => setItem(sIdx, iIdx, 'unit_price', e.target.value)}
                              className="col-span-2 border-[#E6EBE8] bg-white text-right text-sm"
                              data-testid={`co-item-price-${sIdx}-${iIdx}`}
                            />
                            <div className={`col-span-1 py-2 text-right text-sm font-medium ${itemIsCredit ? 'text-red-600' : 'text-[#1A1D1C]'}`}
                                 style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                              {line < 0 ? '−' : ''}${Math.abs(line).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div className="col-span-2 flex items-center justify-end gap-0.5">
                              <button
                                onClick={() => toggleCredit(sIdx, iIdx)}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                                  itemIsCredit
                                    ? 'bg-red-50 text-red-700 border-red-200'
                                    : 'bg-[#F0F3F1] text-[#6B7A73] border-[#E6EBE8] hover:text-[#1A1D1C]'
                                }`}
                                title={itemIsCredit ? 'Convert back to charge' : 'Convert to credit'}
                                data-testid={`co-item-credit-toggle-${sIdx}-${iIdx}`}
                              >
                                {itemIsCredit
                                  ? (editorLang === 'fr' ? 'Crédit' : 'Credit')
                                  : (editorLang === 'fr' ? 'Charge' : 'Charge')}
                              </button>
                              <button
                                onClick={() => removeItem(sIdx, iIdx)}
                                className="p-1 rounded text-[#6B7A73] hover:text-red-600 hover:bg-red-50"
                                title={editorLang === 'fr' ? 'Supprimer' : 'Remove'}
                                data-testid={`co-item-remove-${sIdx}-${iIdx}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <div className="flex items-center justify-between pt-2 border-t border-[#F0F3F1]">
                      <Button
                        onClick={() => addItem(sIdx)}
                        variant="ghost"
                        size="sm"
                        className="text-[#2D6B68] hover:bg-[#F0F3F1]"
                        data-testid={`co-add-item-${sIdx}`}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> {editorLang === 'fr' ? 'Ajouter une ligne' : 'Add line'}
                      </Button>
                      <p className={`text-sm font-medium ${sIsCredit ? 'text-red-600' : 'text-[#1A1D1C]'}`}
                         style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {editorLang === 'fr' ? 'Sous-total' : 'Subtotal'}: {sTotal < 0 ? '−' : ''}${Math.abs(sTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                );
              }}
            />
            <Button
              onClick={addSection}
              variant="outline"
              className="w-full border-dashed border-[#E6EBE8] text-[#2D6B68] hover:bg-[#F0F3F1]"
              data-testid="co-add-section-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> {editorLang === 'fr' ? 'Ajouter une section' : 'Add section'}
            </Button>
          </div>

          {/* Totals card */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">
              {editorLang === 'fr' ? 'Résumé de l\'ajustement' : 'Adjustment Summary'}
            </h2>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">{editorLang === 'fr' ? 'Ajustement net (avant taxes)' : 'Net Adjustment (before tax)'}</span>
                <span className={`font-medium ${subtotal < 0 ? 'text-red-600' : 'text-[#1A1D1C]'}`}
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {subtotal < 0 ? '−' : ''}${Math.abs(subtotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">{editorLang === 'fr' ? `TPS (${co.tax_gst || 5}%)` : `GST (${co.tax_gst || 5}%)`}</span>
                <span className={gst < 0 ? 'text-red-600' : 'text-[#1A1D1C]'}
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {gst < 0 ? '−' : ''}${Math.abs(gst).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">{editorLang === 'fr' ? `TVQ (${co.tax_qst || 9.975}%)` : `QST (${co.tax_qst || 9.975}%)`}</span>
                <span className={qst < 0 ? 'text-red-600' : 'text-[#1A1D1C]'}
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {qst < 0 ? '−' : ''}${Math.abs(qst).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="h-px bg-[#E6EBE8] my-2" />
              <div className="flex justify-between text-base">
                <span className="font-semibold text-[#1A1D1C]">
                  {isCredit
                    ? (editorLang === 'fr' ? 'Crédit total (taxes incluses)' : 'Total Credit (inc. tax)')
                    : (editorLang === 'fr' ? 'Ajustement total (taxes incluses)' : 'Total Adjustment (inc. tax)')}
                </span>
                <span className={`font-bold ${isCredit ? 'text-red-600' : 'text-[#2D6B68]'}`}
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {isCredit ? '−' : ''}${Math.abs(total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            {isCredit && (
              <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-900">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span>
                  {editorLang === 'fr'
                    ? <>Cet avenant a un total négatif — il sera affiché comme un <strong>crédit</strong> sur le PDF.</>
                    : <>This change order has a negative total — it will be displayed as a <strong>credit</strong> on the PDF.</>}
                </span>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6 space-y-2">
            <label className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">
              {editorLang === 'fr' ? 'Notes internes' : 'Internal Notes'}
            </label>
            <Textarea
              data-testid="co-notes-input"
              value={currentNotes}
              onChange={e => setLangField('notes', e.target.value)}
              placeholder={editorLang === 'fr' ? 'Notes internes (non imprimées sur le PDF)' : 'Internal notes (not printed on PDF)'}
              rows={3}
              className="bg-white border-[#E6EBE8] resize-none"
            />
          </div>
        </div>

        {/* Preview panel */}
        {showPreview && (
          <div className="w-1/2 sticky top-0 h-[calc(100vh-120px)]" data-testid="co-preview-panel">
            <div className="bg-white border border-[#E6EBE8] rounded-md h-full flex flex-col">
              <div className="px-4 py-3 border-b border-[#E6EBE8] flex items-center justify-between flex-shrink-0">
                <h3 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">
                  PDF Preview ({editorLang.toUpperCase()})
                </h3>
                <Button
                  variant="ghost"
                  onClick={loadPreview}
                  disabled={previewLoading}
                  className="text-xs text-[#2D6B68] hover:bg-[#F0F3F1]"
                  data-testid="co-refresh-preview-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${previewLoading ? 'animate-spin' : ''}`} />
                  {previewLoading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
              <div className="flex-1 overflow-hidden">
                {previewLoading && !pdfUrl ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : pdfUrl ? (
                  <iframe
                    src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitH&pagemode=none`}
                    className="w-full h-full border-0"
                    title="CO PDF Preview"
                    data-testid="co-pdf-preview-iframe"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-[#6B7A73] text-sm">
                    <p>Save, then click Refresh to preview.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Leave dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Unsaved changes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">You have unsaved changes on this change order. Leave anyway?</p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="border-[#E6EBE8]"
              onClick={() => { setShowLeaveDialog(false); setPendingNav(null); }}
              data-testid="co-stay-btn"
            >
              Stay
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => { setShowLeaveDialog(false); if (pendingNav) navigate(pendingNav); }}
              data-testid="co-leave-btn"
            >
              Leave without saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
